/**
 * HolidaysTab Component
 *
 * Story 9-13: Feestdagen Kalender
 * Manages the holidays calendar for the organization.
 */

import { useState } from 'react';
import { useHolidays, useCreateHoliday, useDeleteHoliday } from '../useSettings';
import type { CreateHolidayRequest } from '@functieloonhuis/shared';

const currentYear = new Date().getFullYear();
const yearOptions = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

export function HolidaysTab() {
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [isAdding, setIsAdding] = useState(false);
  const [newHoliday, setNewHoliday] = useState<CreateHolidayRequest>({
    name: '',
    date: '',
    isRecurring: false,
    country: 'NL',
  });
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { data: holidays, isLoading, error } = useHolidays(selectedYear);
  const createHoliday = useCreateHoliday();
  const deleteHoliday = useDeleteHoliday();

  const handleCreate = async () => {
    if (!newHoliday.name.trim() || !newHoliday.date) return;

    try {
      await createHoliday.mutateAsync(newHoliday);
      setNewHoliday({ name: '', date: '', isRecurring: false, country: 'NL' });
      setIsAdding(false);
    } catch (err) {
      console.error('Failed to create holiday:', err);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteHoliday.mutateAsync(id);
      setDeleteId(null);
    } catch (err) {
      console.error('Failed to delete holiday:', err);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  const getDayOfWeek = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDay();
    return day === 0 || day === 6; // weekend
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        Fout bij laden van feestdagen: {error.message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Feestdagen Kalender</h2>
          <p className="mt-1 text-sm text-gray-500">
            Beheer officiële feestdagen en vrije dagen voor de organisatie
          </p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <button
            onClick={() => setIsAdding(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Feestdag toevoegen
          </button>
        </div>
      </div>

      {/* Add Holiday Form */}
      {isAdding && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-4">Nieuwe feestdag toevoegen</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Naam</label>
              <input
                type="text"
                value={newHoliday.name}
                onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                placeholder="bijv. Koningsdag"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Datum</label>
              <input
                type="date"
                value={newHoliday.date}
                onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={newHoliday.isRecurring}
                  onChange={(e) => setNewHoliday({ ...newHoliday, isRecurring: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                />
                Jaarlijks terugkerend
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCreate}
                disabled={createHoliday.isPending}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {createHoliday.isPending ? 'Opslaan...' : 'Toevoegen'}
              </button>
              <button
                onClick={() => {
                  setIsAdding(false);
                  setNewHoliday({ name: '', date: '', isRecurring: false, country: 'NL' });
                }}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Holidays List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {holidays && holidays.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {holidays.map((holiday) => (
              <li
                key={holiday.id}
                className={`px-6 py-4 flex items-center justify-between ${
                  getDayOfWeek(holiday.date) ? 'bg-orange-50' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`
                    w-12 h-12 rounded-lg flex flex-col items-center justify-center
                    ${getDayOfWeek(holiday.date) ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'}
                  `}>
                    <span className="text-xs font-medium uppercase">
                      {new Date(holiday.date).toLocaleDateString('nl-NL', { month: 'short' })}
                    </span>
                    <span className="text-lg font-bold">
                      {new Date(holiday.date).getDate()}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      {holiday.name}
                      {holiday.isRecurring && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Terugkerend
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-500">{formatDate(holiday.date)}</p>
                    {getDayOfWeek(holiday.date) && (
                      <p className="text-xs text-orange-600 mt-1">Valt in het weekend</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {deleteId === holiday.id ? (
                    <>
                      <span className="text-sm text-gray-500 mr-2">Verwijderen?</span>
                      <button
                        onClick={() => handleDelete(holiday.id)}
                        disabled={deleteHoliday.isPending}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Ja
                      </button>
                      <button
                        onClick={() => setDeleteId(null)}
                        className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                      >
                        Nee
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setDeleteId(holiday.id)}
                      className="text-gray-400 hover:text-red-600"
                      title="Verwijderen"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="px-6 py-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Geen feestdagen</h3>
            <p className="mt-1 text-sm text-gray-500">
              Er zijn nog geen feestdagen gedefinieerd voor {selectedYear}.
            </p>
            <button
              onClick={() => setIsAdding(true)}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-50 hover:bg-blue-100"
            >
              Eerste feestdag toevoegen
            </button>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex gap-3">
          <svg className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-900">Over feestdagen</p>
            <ul className="mt-1 list-disc list-inside space-y-1">
              <li>Feestdagen worden gebruikt bij het berekenen van werkdagen en deadlines</li>
              <li>Terugkerende feestdagen worden automatisch elk jaar overgenomen</li>
              <li>Feestdagen die in het weekend vallen worden oranje gemarkeerd</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
